import React, { useCallback, useEffect, useState } from "react";
import { Phone, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/utils/api";

/** Mobile numbers that can log in as this customer. Staff can see them; only admins can add or remove. */
export default function CustomerPhonesCard({ customerId, isAdmin }) {
  const [d, setD] = useState(null);
  const [phone, setPhone] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/v2/customer-phones/${customerId}`).then((r) => setD(r.data)).catch(() => setD({ primary: "", extra: [], error: true }));
  }, [customerId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!phone.trim()) return;
    setBusy(true);
    try {
      const r = await api.post(`/v2/customer-phones/${customerId}`, { phone: phone.trim(), label: label.trim() });
      setD(r.data); setPhone(""); setLabel(""); toast.success("Mobile number added - it can now be used to log in");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add the number"); }
    setBusy(false);
  };
  const remove = async (p) => {
    if (!window.confirm(`Remove ${p}? It will no longer be able to log in as this customer.`)) return;
    try { const r = await api.delete(`/v2/customer-phones/${customerId}/${p}`); setD(r.data); toast.success("Number removed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove the number"); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="customer-phones-card">
      <div className="flex items-center gap-2 mb-3"><Phone className="w-4 h-4 text-slate-500" /><h3 className="font-semibold text-slate-800 text-sm">Mobile numbers with login access</h3></div>
      {!d ? <Loader2 className="w-4 h-4 animate-spin" /> : (
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2"><span className="font-mono">{d.primary || "—"}</span><span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">Main</span></div>
          {d.alternate_at_creation ? <div className="flex items-center gap-2"><span className="font-mono">{d.alternate_at_creation}</span><span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">Alternate (set at creation)</span></div> : null}
          {d.extra.map((x) => (
            <div key={x.phone} className="flex items-center gap-2" data-testid={`extra-phone-${x.phone}`}>
              <span className="font-mono">{x.phone}</span>
              <span className="text-[11px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">{x.label || "Extra"}</span>
              {x.added_by && <span className="text-[11px] text-slate-400">added by {x.added_by}</span>}
              {isAdmin && <button onClick={() => remove(x.phone)} title="Remove this number" aria-label={`Remove ${x.phone}`} className="ml-auto w-5 h-5 flex items-center justify-center rounded-full text-red-600 hover:bg-red-50"><X className="w-3.5 h-3.5" /></button>}
            </div>
          ))}
          {isAdmin ? (
            <div className="flex gap-2 pt-2 flex-wrap">
              <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="10-digit mobile" inputMode="numeric" data-testid="extra-phone-input"
                className="border border-slate-300 rounded px-2 py-1 text-sm w-40" />
              <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (e.g. accounts)" maxLength={40}
                className="border border-slate-300 rounded px-2 py-1 text-sm w-44" />
              <button onClick={add} disabled={busy || !phone.trim()} data-testid="extra-phone-add"
                className="px-3 py-1 text-sm font-semibold rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">{busy ? "Adding..." : "Add number"}</button>
            </div>
          ) : <div className="text-[11px] text-slate-400 pt-1">Only an admin can add or remove numbers.</div>}
          <div className="text-[11px] text-slate-400">Anyone with one of these numbers can log in as this customer with a one-time code. The main number is changed from Edit.</div>
        </div>
      )}
    </div>
  );
}
